import { ModernPaginationProps } from '@moneyforward/ap-frontend-components';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// Generic type for invoice report column data
export interface BaseInvoiceReportColumn {
  id: string;
  [key: string]: unknown;
}

// Generic search query type
export interface BaseSearchQuery {
  page?: number;
  [key: string]: unknown;
}

type TriggerFindTargetReportProps<
  T extends BaseInvoiceReportColumn,
  Q extends BaseSearchQuery
> = {
  children: React.ReactNode;
  data: T[];
  pagination: ModernPaginationProps;
  selectedIndex: number | undefined;
  setSelectedIndex: (index: number | undefined) => void;
  onSearch: (queryParams: Q) => void;
  isDataLoading: boolean;
};

type TriggerFindTargetReportContextType<T extends BaseInvoiceReportColumn> = {
  isTriggering: boolean;
  onTriggerFindTargetReport: (
    onSuccessFoundTargetReport: (report: T) => void,
    onErrorFoundTargetReport: () => void
  ) => void;
  onTriggerBackToPageOneIfNoData: () => void;
  selectedIndex: number | undefined;
  setSelectedIndex: (index: number | undefined) => void;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Context = createContext<TriggerFindTargetReportContextType<any> | null>(
  null
);

export function TriggerFindTargetReportProvider<
  T extends BaseInvoiceReportColumn,
  Q extends BaseSearchQuery
>({
  children,
  data,
  pagination,
  selectedIndex,
  setSelectedIndex,
  onSearch,
  isDataLoading,
}: TriggerFindTargetReportProps<T, Q>): JSX.Element {
  const [isTriggering, setIsTriggering] = useState<boolean>(false);
  const [isTriggeringBackToPageOne, setIsTriggeringBackToPageOne] =
    useState<boolean>(false);

  const onSuccessFoundTargetReportRef = useRef<((report: T) => void) | null>(
    null
  );
  const onErrorFoundTargetReportRef = useRef<(() => void) | null>(null);

  const onClearState = useCallback(() => {
    setIsTriggering(false);
    onSuccessFoundTargetReportRef.current = null;
    onErrorFoundTargetReportRef.current = null;
  }, []);

  const onTriggerFindTargetReport = useCallback(
    (
      onSuccessFoundTargetReport?: (report: T) => void,
      onErrorFoundTargetReport?: () => void
    ) => {
      if (isTriggering) return;
      onSuccessFoundTargetReportRef.current =
        onSuccessFoundTargetReport ?? null;
      onErrorFoundTargetReportRef.current = onErrorFoundTargetReport ?? null;
      setIsTriggering(true);
    },
    [isTriggering]
  );

  const onTriggerBackToPageOneIfNoData = useCallback(() => {
    setIsTriggeringBackToPageOne(true);
  }, []);

  /** Effect find target report */
  useEffect(() => {
    if (!isTriggering || selectedIndex === undefined || isDataLoading) return;

    if (!data.length && pagination.currentPage === 1) {
      onErrorFoundTargetReportRef.current?.();
      onClearState();
      return;
    }
    /** Trigger go back to page one if no data */
    if (!data.length && pagination.currentPage !== 1) {
      onTriggerBackToPageOneIfNoData();
      return;
    }

    if (data.length && data.length - 1 >= selectedIndex) {
      onSuccessFoundTargetReportRef.current?.(data[selectedIndex]!);
      onClearState();
      return;
    }

    onSuccessFoundTargetReportRef.current?.(data[0]!);
    onClearState();
  }, [
    isTriggering,
    data,
    selectedIndex,
    pagination,
    setSelectedIndex,
    onClearState,
    onTriggerBackToPageOneIfNoData,
    isDataLoading,
  ]);

  /** Effect go back to page one if no data */
  useEffect(() => {
    if (!isTriggeringBackToPageOne) return;
    if (!data.length && pagination.currentPage !== 1) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      onSearch({ page: 1 } as Q);
      setSelectedIndex(0);
      return;
    }
    setIsTriggeringBackToPageOne(false);
  }, [
    data,
    pagination.currentPage,
    onSearch,
    setSelectedIndex,
    isTriggeringBackToPageOne,
  ]);

  const value = useMemo(
    () => ({
      isTriggering,
      onTriggerFindTargetReport,
      onTriggerBackToPageOneIfNoData,
      selectedIndex,
      setSelectedIndex,
    }),
    [
      isTriggering,
      onTriggerFindTargetReport,
      onTriggerBackToPageOneIfNoData,
      selectedIndex,
      setSelectedIndex,
    ]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useTriggerFindTargetReport<
  T extends BaseInvoiceReportColumn
>(): TriggerFindTargetReportContextType<T> {
  const context = useContext(Context);

  if (!context) {
    throw new Error(
      'useTriggerFindTargetReport must be used within a TriggerFindTargetReportProvider'
    );
  }

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return context as unknown as TriggerFindTargetReportContextType<T>;
}
